import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { To, useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteLessonPlanMutation,
  useGetLessonPlanQuery
} from '../../../../../services/endpoints/content/lessonPlan';
import { getCurrentUser } from '../../../../../services/helper';
import { LessonPlan } from '../../../../../types/lessonPlan.type';
import { getErrorMessage } from '../../../../../utils/utils';
import {
  userCanDelete_LessonPlan,
  userCanEditCreateDelete_LessonPlan
} from './LessonPlansView';

export const LessonPlanDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const currentUser = getCurrentUser();
  const { data, error, isLoading } = useGetLessonPlanQuery(+params.id!);
  const [deleteLessonPlan, { isLoading: isDeleting }] = useDeleteLessonPlanMutation();
  const [lessonPlan, setLessonPlan] = useState<LessonPlan>();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading lesson plan', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setLessonPlan(data.lessonPlan);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteLessonPlan(+params.id!).unwrap();
      addToast('Lesson plan deleted successfully', 'success');
      navigate('/admin/content/lesson-plans');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !lessonPlan) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate(-1 as To)}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Lesson Plans List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Lesson Plan #${lessonPlan?.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          {userCanEditCreateDelete_LessonPlan(currentUser) && (
            <SimpleButton
              variant="primary"
              height={36}
              onClick={() => navigate(`/admin/content/lesson-plans/${lessonPlan?.id}/edit`)}
            >
              Edit Lessons plan
            </SimpleButton>
          )}
          {userCanDelete_LessonPlan(currentUser) && (
            <SimpleButton
              variant="primary"
              height={36}
              onClick={() => setShowModal(true)}
            >
              Delete Lessons plan
            </SimpleButton>
          )}
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Id:"
              text={lessonPlan?.id}
            />
            <LabelText
              label="Label:"
              text={lessonPlan?.name}
            />
            <LabelText
              label="Slug:"
              text={lessonPlan?.slug}
            />
          </DetailSection>
        </Stack>
      </div>

      <ConfirmationModal
        show={showModal}
        title="Delete Lesson Plan"
        message="Are you sure you want to remove this lessons plan?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
